import { useRef, useEffect } from 'react';

const ITEM_HEIGHT = 40;
const PADDING_TOP = 96;

export interface ColumnProps {
  items: string[];
  selected: string;
  onSelect: (val: string) => void;
  totalSlots?: number;
  isOpen: boolean;
  /** When true, column repeats items infinitely (e.g. hours 1–12, minutes 00–55) */
  loop?: boolean;
}

export const Column = ({
  items,
  selected,
  onSelect,
  totalSlots,
  isOpen,
  loop = false,
}: ColumnProps) => {
  const paddedItems: string[] = loop
    ? [...items, ...items, ...items]
    : (() => {
        const slotCount = totalSlots ?? items.length;
        const out: string[] = [];
        for (let i = 0; i < slotCount; i++) out.push(i < items.length ? items[i] : '');
        return out;
      })();
  const blockHeight = items.length * ITEM_HEIGHT;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commitSelectionRef = useRef<() => void>(() => {});

  const updateWheelTransforms = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const containerRect = scroller.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    const maxSteps = 4;

    const children = Array.from(
      scroller.querySelectorAll<HTMLButtonElement>('button[data-wheel-item="1"]'),
    );
    for (const el of children) {
      const r = el.getBoundingClientRect();
      const elCenter = r.top + r.height / 2;
      const deltaPx = elCenter - centerY;
      const steps = deltaPx / ITEM_HEIGHT;
      const clamped = Math.max(-maxSteps, Math.min(maxSteps, steps));

      const abs = Math.abs(clamped);
      const rotateX = clamped * 18;
      const scale = 1 - Math.min(0.22, abs * 0.06);
      const opacity = 1 - Math.min(0.65, abs * 0.14);

      el.style.transform = `perspective(700px) rotateX(${rotateX}deg) scale(${scale})`;
      el.style.opacity = `${opacity}`;
    }
  };

  const scheduleWheelUpdate = () => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updateWheelTransforms();
    });
  };

  // After user stops scrolling, select whichever item is centered
  const selectCenteredItem = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const containerRect = scroller.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;

    const buttons = Array.from(
      scroller.querySelectorAll<HTMLButtonElement>('button[data-wheel-item="1"]'),
    );

    let closestBtn: HTMLButtonElement | null = null;
    let minDist = Infinity;

    for (const btn of buttons) {
      if (btn.disabled) continue;
      const r = btn.getBoundingClientRect();
      const btnCenter = r.top + r.height / 2;
      const dist = Math.abs(btnCenter - centerY);
      if (dist < minDist) {
        minDist = dist;
        closestBtn = btn;
      }
    }

    const val = closestBtn?.textContent?.trim();
    if (val && val !== selected) {
      onSelect(val);
    }
  };

  const scrollToSelected = (behavior: ScrollBehavior = 'smooth') => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const idxInItems = items.indexOf(selected);
    const selectedIndex = loop && idxInItems >= 0
      ? items.length + idxInItems
      : paddedItems.findIndex((item) => item === selected);
    if (selectedIndex === -1) return;

    const containerHeight = scroller.clientHeight;
    const centerY = containerHeight / 2;
    const itemCenterY = PADDING_TOP + selectedIndex * ITEM_HEIGHT + ITEM_HEIGHT / 2;
    const targetScrollTop = itemCenterY - centerY;
    const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const clamped = Math.min(maxScrollTop, Math.max(0, targetScrollTop));

    if (behavior === 'instant') {
      scroller.scrollTop = clamped;
    } else {
      scroller.scrollTo({ top: clamped, behavior: 'smooth' });
    }
  };

  const commitSelection = () => {
    if (scrollEndTimer.current) {
      clearTimeout(scrollEndTimer.current);
      scrollEndTimer.current = null;
    }
    selectCenteredItem();
  };
  commitSelectionRef.current = commitSelection;

  const handleScroll = () => {
    scheduleWheelUpdate();

    // Loop: keep scroll position in the middle block so we can wrap infinitely
    if (loop && scrollerRef.current) {
      const scroller = scrollerRef.current;
      const { scrollTop, clientHeight } = scroller;
      if (scrollTop < PADDING_TOP + blockHeight) {
        scroller.scrollTop = scrollTop + blockHeight;
      } else if (scrollTop > PADDING_TOP + 2 * blockHeight - clientHeight) {
        scroller.scrollTop = scrollTop - blockHeight;
      }
    }

    // Debounce fallback: 400ms after last scroll (so snap animation has settled).
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(commitSelection, 400);
  };

  // On open: spin from top down to selected item (loop: start in middle block)
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;

      if (loop) {
        const idxInItems = items.indexOf(selected);
        const startIndex = idxInItems >= 0 ? items.length + idxInItems : items.length;
        const centerY = scroller.clientHeight / 2;
        const itemCenterY = PADDING_TOP + startIndex * ITEM_HEIGHT + ITEM_HEIGHT / 2;
        scroller.scrollTop = Math.max(0, itemCenterY - centerY);
      } else {
        scroller.scrollTop = 0;
      }
      updateWheelTransforms();

      requestAnimationFrame(() => {
        scrollToSelected('smooth');
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // When selected changes externally: smooth-scroll to keep in sync
  const prevSelectedRef = useRef<string>(selected);
  useEffect(() => {
    if (!isOpen) return;
    if (prevSelectedRef.current === selected) return;
    prevSelectedRef.current = selected;

    scrollToSelected('smooth');
    const t = window.setTimeout(scheduleWheelUpdate, 120);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, isOpen]);

  // Commit selection as soon as scroll truly ends (scrollend = after snap). Fallback is debounce in handleScroll.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onScrollEnd = () => commitSelectionRef.current();
    scroller.addEventListener('scrollend', onScrollEnd);
    return () => scroller.removeEventListener('scrollend', onScrollEnd);
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (scrollEndTimer.current != null) clearTimeout(scrollEndTimer.current);
    };
  }, []);

  return (
    <div
      ref={scrollerRef}
      onScroll={handleScroll}
      className="tp-column"
    >
      {paddedItems.map((item, idx) => {
        const isPlaceholder = item === '';
        const isActive = item === selected;
        const classes = [
          'tp-column__item',
          isActive ? 'tp-column__item--active' : '',
          isPlaceholder ? 'tp-column__item--placeholder' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={loop ? `loop-${idx}` : isPlaceholder ? `pad-${idx}` : item}
            onClick={(e) => {
              e.stopPropagation();
              if (!isPlaceholder) onSelect(item);
            }}
            data-wheel-item="1"
            disabled={isPlaceholder}
            className={classes}
          >
            {item}
          </button>
        );
      })}
    </div>
  );
};
